// Persistent in-app notification feed (TT-6783).
//
// Single source of truth for the notification center and its bell badge. The
// backend persists one short-lived notification per event (3DS challenges expire
// at their maxResponseTime and drop off the feed automatically), scoped to the
// authenticated user.
import {callAPI} from '@/store/utils';

const FYGR_COMPANY = 'FYGR_COMPANY';
const CARD_3DS_VALIDATION_REQUESTED = 'card_3ds_validation_requested';
const UNREAD = 'unread';
const READ = 'read';

interface NotificationItem {
  id?: string | number;
  state?: string;
  event?: string;
  payload?: {acs_transaction_id?: string; [key: string]: unknown};
  [key: string]: unknown;
}

interface NotificationsState {
  // Feed as returned by `listNotifications` (unread + read, newest-first).
  items: NotificationItem[];
}

export default {
  namespaced: true,

  state: (): NotificationsState => ({
    items: [],
  }),

  getters: {
    items: (state: NotificationsState): NotificationItem[] => state.items,
    unreadCount: (state: NotificationsState): number =>
      state.items.filter((n) => n?.state === UNREAD).length,
  },

  mutations: {
    setNotifications(state: NotificationsState, items: NotificationItem[]) {
      state.items = Array.isArray(items) ? items : [];
    },
    // Optimistically flip a single notification to read (keeps the badge/center
    // in sync without a refetch).
    markRead(state: NotificationsState, notificationId: NotificationItem['id']) {
      state.items = state.items.map((n) =>
        n?.id === notificationId ? {...n, state: READ} : n,
      );
    },
  },

  actions: {
    async actionListNotifications({commit, rootState}, params = {}) {
      const companyId = rootState.companyId || localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'listNotifications', {
        companyId,
        ...params,
      });
      if (Array.isArray(res?.result)) {
        commit('setNotifications', res.result);
      }
      return res;
    },

    async actionSetNotificationAsRead({commit, rootState}, {notificationId}) {
      if (!notificationId) return null;
      const companyId = rootState.companyId || localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'setNotificationAsRead', {
        companyId,
        notificationId,
      });
      if (res?.error === false) {
        commit('markRead', notificationId);
      }
      return res;
    },

    // Resolve the single notification tied to a 3DS challenge. The backend keeps
    // exactly one `card_3ds_validation_requested` notification per challenge, so
    // matching on the payload's acs_transaction_id yields at most one id.
    async actionGetNotificationIdByAcsTransactionId({dispatch}, acsTransactionId) {
      if (!acsTransactionId) return null;
      const res = await dispatch('actionListNotifications');
      const items: NotificationItem[] = Array.isArray(res?.result) ? res.result : [];
      const match = items.find(
        (n) =>
          n?.event === CARD_3DS_VALIDATION_REQUESTED &&
          n?.payload?.acs_transaction_id === acsTransactionId,
      );
      return match?.id ?? null;
    },
  },
};
